/**
 * Event Types
 * Types for event management, seminars, and webinars
 */

import type { BaseEntity, UUID } from "./common.types";

// Event status enum. Values MUST match the string literals stored in
// events.status (scheduled/in_progress/completed/cancelled). SCHEDULED,
// IN_PROGRESS, and POSTPONED were referenced by EVENT_STATUSES/get­StatusColor
// but were missing here — so those dropdown options emitted `undefined` and
// their list filters silently fell back to "all". Added 2026-08 so the status
// filter actually works.
export enum EventStatus {
  PUBLISHED = "published",
  ACTIVE = "active",
  SCHEDULED = "scheduled",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  POSTPONED = "postponed",
}

// Event type enum
export enum EventType {
  SHOW = "show",
  CLASS = "class",
  TABLE = "table",
  SEMINAR = "seminar",
  CONFERENCE = "conference",
  SPEAKING_ENGAGEMENT = "speaking_engagement",
  WEBINAR = "webinar",
  GUN_SHOW = "gun_show",
  PRIVATE_EVENT = "private_event",
  COMMUNITY_EVENT = "community_event",
  MEETING = "meeting",
  TRAINING = "training",
  CALL = "call",
  SOCIAL = "social",
  OTHER = "other",
  // Smart Admin replacement R1 (2026-05-17). Phone Sale belongs in a
  // future sales_activities model, NOT here — intentionally excluded.
  NETWORKING_EVENT = "networking_event",
  // Classification-driven create flow (2026-07-28): booth-based, no-pre-registration
  // recruitment events (conversion=Yes / registration=No), same family as gun_show.
  MEMBER_CHECK_IN = "member_check_in",
  LIGHTNING_DEALS = "lightning_deals",
  // Range Night (2026-07-31): member-facing conversion event, RSVP/registration-based
  // (guest invites + vouchers + member conversion). "Full box" family like conference.
  RANGE_NIGHT = "range_night",
}

// Event types that show the "Voucher Used" toggle
export const VOUCHER_USED_EVENT_TYPES: string[] = [
  EventType.TABLE,
  EventType.GUN_SHOW,
  EventType.CONFERENCE,
  EventType.SPEAKING_ENGAGEMENT,
  EventType.MEMBER_CHECK_IN,
  EventType.LIGHTNING_DEALS,
  EventType.RANGE_NIGHT,
];

// Phase 1.1 Ship 3 — event types where a sales-rep QR signup makes
// sense, so the create form defaults subscription_signup_eligible=true
// and the DB trigger auto-mints a generic CTA. Operators can override
// after create via the standard edit form. Excludes phone_sale,
// networking_event, training, webinar, call, social, other — those
// don't have a public event signup pattern.
// 2026-10-29 (owner): class, table, seminar and speaking_engagement removed -- they get no event CTA and use the
// venue's QR codes (NO_PROMO_EVENT_TYPES in utils/eventTypePolicy.ts). Every consumer of this list only decides
// whether to mint / finish the generic CTA (EventForm and EventCreateWizard: subscription_signup_eligible on create,
// and afterEventLinksSaved). The database forces the same (migration 2026-10-29-1260).
export const SUBSCRIPTION_ELIGIBLE_EVENT_TYPES: string[] = [
  EventType.GUN_SHOW,
  EventType.CONFERENCE,
  EventType.SHOW,
  // New conversion/recruitment types auto-mint a generic CTA like gun_show — the
  // CTA is the primary member-recruitment lever at these events (do not interfere).
  EventType.MEMBER_CHECK_IN,
  EventType.LIGHTNING_DEALS,
  EventType.RANGE_NIGHT,
];

// Classification for the create-flow toggles (2026-07-28). Maps each create-visible
// event type to its (conversion engine?, registration required?) quadrant. Drives which
// types the classification toggles surface and the capability defaults. Adjust freely —
// this is UI-level config, not stored state (conversion-ness derives from `type`).
export const EVENT_TYPE_CLASSIFICATION: Record<
  string,
  { conversion: boolean; registration: boolean }
> = {
  [EventType.GUN_SHOW]: { conversion: true, registration: false },
  [EventType.TABLE]: { conversion: true, registration: false },
  [EventType.MEMBER_CHECK_IN]: { conversion: true, registration: false },
  [EventType.LIGHTNING_DEALS]: { conversion: true, registration: false },
  [EventType.CLASS]: { conversion: false, registration: true },
  [EventType.SEMINAR]: { conversion: false, registration: true },
  // Full box: a registered event that is ALSO a conversion opportunity.
  [EventType.CONFERENCE]: { conversion: true, registration: true },
  // Range Night: RSVP-based member event with guest invites + vouchers + conversion.
  [EventType.RANGE_NIGHT]: { conversion: true, registration: true },
  [EventType.SPEAKING_ENGAGEMENT]: { conversion: false, registration: false },
  [EventType.NETWORKING_EVENT]: { conversion: false, registration: false },
};

// Cancellation reasons — R6 (2026-05-17): expanded to the 12 codes Heather
// specified on PDF page 9. Source of truth is the event_cancellation_reasons
// DB lookup table (FK from events.cancellation_reason). Keep this constant
// in sync with the table seed.
export const CANCELLATION_REASONS = [
  { value: "duplicate_event", label: "Duplicate Event" },
  { value: "no_rep_available", label: "No Rep Available" },
  {
    value: "low_enrollment_organization",
    label: "Low Enrollment – Organization",
  },
  { value: "low_enrollment_usls", label: "Low Enrollment – USLS" },
  { value: "instructor_unavailable", label: "Instructor Unavailable" },
  { value: "medical_organization", label: "Medical – Organization" },
  { value: "medical_usls", label: "Medical – USLS" },
  { value: "weather_organization", label: "Weather – Organization" },
  { value: "weather_usls", label: "Weather – USLS" },
  { value: "venue_issue", label: "Venue Issue" },
  { value: "holiday_organization", label: "Holiday – Organization" },
  { value: "other", label: "Other" },
] as const;

export type CancellationReason = (typeof CANCELLATION_REASONS)[number]["value"];

// Class delivery methods
export const CLASS_DELIVERY_METHODS = [
  { value: "in_person", label: "In-Person" },
  { value: "online", label: "Online (Live Virtual)" },
  { value: "hybrid", label: "Hybrid" },
  { value: "marketing_video", label: "Marketing Video" },
] as const;

export type ClassDeliveryMethod =
  (typeof CLASS_DELIVERY_METHODS)[number]["value"];

// Curriculum options (Smart Admin replacement R1: renamed from "USLS
// Curriculum" to just "Curriculum" since not all curriculums are USLS;
// the USLS_CURRICULUM_USED boolean still toggles whether THIS event
// used USLS material). Added "Instructor Class" per Heather page 2.
export const USLS_CURRICULUM_OPTIONS = [
  { value: "concealed_carry", label: "Concealed Carry" },
  { value: "home_defense", label: "Home Defense" },
  { value: "legal_basics", label: "Legal Basics" },
  { value: "firearms_safety", label: "Firearms Safety" },
  { value: "self_defense", label: "Self Defense" },
  { value: "advanced_tactics", label: "Advanced Tactics" },
  { value: "security", label: "Security" },
  { value: "instructor_class", label: "Instructor Class" },
  { value: "other", label: "Other" },
] as const;

// Class subtypes
export const CLASS_SUBTYPES = [
  { value: "initial", label: "Initial" },
  { value: "renewal", label: "Renewal" },
  { value: "advanced", label: "Advanced" },
  { value: "specialty", label: "Specialty" },
] as const;

// Event ownership
export const EVENT_OWNERSHIP_OPTIONS = [
  { value: "usls_owned", label: "USLS Owned" },
  { value: "partner_owned", label: "Partner Owned" },
  { value: "co_hosted", label: "Co-Hosted" },
] as const;

export type EventOwnership = (typeof EVENT_OWNERSHIP_OPTIONS)[number]["value"];

// Recurrence frequency
export enum RecurrenceFrequency {
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
  YEARLY = "yearly",
}

// Event interface
export interface Event extends BaseEntity {
  title: string;
  description?: string;
  notes?: string;
  type?: string; // Event type: show, class, seminar, conference, webinar
  event_type?: EventType;
  status?: string;
  event_status?: EventStatus;
  // Operator saved the event from the create wizard to finish setting up later
  // (or hand off to someone else). Badged/filterable/sortable in the events list.
  setup_incomplete?: boolean;
  organizer_id?: UUID; // Event owner/organizer - required field
  start_time: string;
  end_time?: string;
  all_day?: boolean;
  date_tbd?: boolean; // When true, date is not yet confirmed and should display as "TBD"
  timezone?: string;
  location_type?: "physical" | "virtual" | "hybrid";
  location_name?: string;
  location_address?:
    | {
        address_line1?: string;
        city?: string;
        state?: string;
        postal_code?: string;
      }
    | string;
  location_city?: string;
  location_state?: string;
  location_postal_code?: string;
  meeting_url?: string;
  virtual_meeting_url?: string;
  capacity?: number;
  max_attendees?: number;
  /** When true, max_attendees is a HARD cap: registrations/check-ins past it are blocked. Soft otherwise. */
  attendance_hard_cap?: boolean;
  /** Smart Admin replacement R3 (2026-05-17): operator-entered actual
   *  attendance count (separate from max_attendees, which is capacity).
   *  Powers the close-rate display on EventDetail. Nullable. */
  attendance_count?: number | null;
  waitlist_capacity?: number;
  /** Item 8: optional gun-show entry/participation fee the org pays — internal tracking +
   *  reimbursement only, never charged to attendees. Distinct from any attendee registration fee. */
  entry_fee?: number | null;
  registration_open?: boolean;
  registration_deadline?: string;
  is_public: boolean;
  is_featured?: boolean;
  is_recurring: boolean;
  registration_required?: boolean;
  /** Soft cap on pre-registrations (members+guests) via the public landing page.
   *  NULL/undefined = unlimited. Never blocks walk-ins (migration 2026-07-28-008). */
  pre_registration_cap?: number | null;
  /** True when held at the org's physical site (enables address auto-fill); N/A for
   *  gun shows (migration 2026-07-28-009). NULL = unspecified. */
  held_on_site?: boolean | null;
  enable_qr_checkin?: boolean;
  allow_walk_ins?: boolean;
  walk_in_limit?: number;
  // Public walk-in member self-check-in page exposure (per-event; default false).
  public_self_checkin_enabled?: boolean;
  recurrence_pattern?: RecurrencePattern;
  organization_id?: UUID;
  created_by?: UUID;
  // Promoter/sponsor organization
  promoter_id?: UUID;
  promoter_name?: string; // Legacy field - use promoter_id instead
  voucher_amount?: number;
  subscription_offer?: string;
  voucher_start_date?: string; // ISO date string
  voucher_end_date?: string; // ISO date string
  voucher_url?: string; // Auto-generated voucher URL
  voucher_qr_url?: string; // URL to the generated QR code image
  // Regulatory state (required for CTAs)
  state?: string; // 2-character US state code for regulatory purposes
  // Cancellation tracking
  cancellation_reason?: CancellationReason;
  cancellation_notes?: string;
  // Class sub-classifications (when type = class)
  class_delivery_method?: ClassDeliveryMethod;
  usls_curriculum?: string;
  class_subtype?: string;
  usls_curriculum_used?: boolean;
  // Voucher used flag (for table, gun_show, conference, speaking_engagement)
  voucher_used?: boolean;
  // Event ownership & sales position assignment
  event_ownership?: EventOwnership;
  sales_position_id?: string;
  // No Rep Required / Self-Service: signed partner runs the event with the
  // generic QR, no lead/event rep. When true, sales_position_id is null.
  is_self_service?: boolean;
  // Denormalized hierarchy position IDs (for reporting)
  territory_manager_position_id?: string;
  regional_manager_position_id?: string;
  sales_director_position_id?: string;
  // Gun Show - Not Advertised (mig 1242, temporary ~90-day option, RM/TM role setup REQ-2.4): a gun show with no
  // Event in a Box invitations and no published landing page; still listed publicly and counted as a gun show.
  not_advertised?: boolean;
  // Joined sales position data (when fetched with select)
  sales_position?: {
    id: string;
    title: string;
    position_type: string;
    assigned_name: string | null;
    parent_position_id: string | null;
  };
  // Public self-check-in containment. Provisioning ticketing defaults this true
  // (check-in OFF until a deliberate "Open venue check-in" action). NOT NULL in the DB.
  self_checkin_disabled?: boolean;
  // Soft delete fields
  is_deleted?: boolean;
  deleted_at?: string;
}

// Event status history
export interface EventStatusHistory extends BaseEntity {
  event_id: UUID;
  from_status?: EventStatus;
  to_status: EventStatus;
  changed_by?: UUID;
  changed_at: string; // TIMESTAMPTZ
  notes?: string;
}

// Event Voucher - matches database schema
export interface EventVoucher extends BaseEntity {
  event_id: UUID;
  voucher_code: string;
  issued_to_attendee_id?: UUID;
  issued_at: string; // TIMESTAMPTZ
  notes?: string;
}

// Event Ticket Tier - for managing different ticket types
export interface EventTicketTier extends BaseEntity {
  event_id: UUID;
  name: string;
  description?: string;
  price: number;
  capacity?: number;
  sort_order: number;
  is_active: boolean;
  // Computed fields
  tickets_sold?: number;
  tickets_available?: number;
}

export interface CreateTicketTierInput {
  event_id: UUID;
  name: string;
  description?: string;
  price?: number;
  capacity?: number;
  sort_order?: number;
  is_active?: boolean;
}

export interface UpdateTicketTierInput {
  name?: string;
  description?: string;
  price?: number;
  capacity?: number;
  sort_order?: number;
  is_active?: boolean;
}

// =====================================================
// HELPER INTERFACES
// =====================================================

// Recurrence pattern (JSON stored in database)
export interface RecurrencePattern {
  frequency: RecurrenceFrequency;
  interval: number; // e.g., every 2 weeks
  days_of_week?: number[]; // 0-6, for weekly recurrence
  day_of_month?: number; // 1-31, for monthly recurrence
  month_of_year?: number; // 1-12, for yearly recurrence
  end_date?: string;
  occurrences?: number; // Number of times to repeat
}

// Event filters
export interface EventFilters {
  event_status?: EventStatus[];
  event_type?: EventType[];
  event_date_from?: string;
  event_date_to?: string;
  is_public?: boolean;
  is_recurring?: boolean;
  search?: string;
}

// Event statistics
export interface EventStats {
  total: number;
  draft: number;
  published: number;
  active: number;
  completed: number;
  cancelled: number;
  upcoming: number;
  past: number;
}

// =====================================================
// PERMISSION TYPES
// =====================================================

/**
 * Permission levels for events (and reusable for campaigns)
 * - owner: Full control - can edit, share, add/remove owners
 * - edit: Can modify event details
 * - view: Read-only access
 */
export enum PermissionLevel {
  OWNER = "owner",
  EDIT = "edit",
  VIEW = "view",
}

/**
 * Event permission record
 * Represents a user's access to a specific event
 */
export interface EventPermission {
  id: UUID;
  event_id: UUID;
  user_id: UUID;
  permission_level: PermissionLevel;
  granted_by?: UUID;
  created_at: string;
  updated_at: string;
  // Joined user data (populated via select query)
  user?: {
    id: UUID;
    full_name: string;
    email: string;
  };
  granted_by_user?: {
    id: UUID;
    full_name: string;
  };
}

/**
 * Input for adding a new permission
 */
export interface AddEventPermissionInput {
  event_id: UUID;
  user_id: UUID;
  permission_level: PermissionLevel;
}

/**
 * Input for updating an existing permission
 */
export interface UpdateEventPermissionInput {
  permission_level: PermissionLevel;
}

/**
 * User's effective permission for an event
 */
export interface UserEventAccess {
  event_id: UUID;
  user_id: UUID;
  permission_level: PermissionLevel;
  is_owner: boolean;
  can_edit: boolean;
  can_manage: boolean;
}

// =====================================================
// CALL TO ACTION TYPES
// =====================================================

/**
 * CTA type
 * - subscription: Uses preset URL for membership signup
 * - registration: Event registration landing page
 * - other: Custom URL
 */
export type CTAType = "subscription" | "registration" | "other";

/**
 * Reward type for CTAs
 * - voucher: Generates voucher codes with amount and validity dates
 * - offer: Link to an existing offer from the offers table
 */
// Schema column event_call_to_actions.reward_type defaults to 'none' and
// accepts any value (no enum constraint). Runtime already uses all five
// values (CTACard.tsx renders "none" / "gift" / "other" alongside
// "voucher" / "offer"); the prior narrower type forced `as RewardType[]`
// casts in CTACard. Widened 2026-06-06 (Sprint A Batch 3 Ship B).
export type RewardType = "none" | "voucher" | "offer" | "gift" | "other";

/**
 * Event Call to Action record
 * Stores CTA configurations with generated QR codes and reward settings
 */
export interface EventCallToAction {
  id: UUID;
  event_id: UUID;
  name: string;
  cta_type: CTAType;
  base_url: string;
  parameters: Record<string, string>;
  generated_url?: string;
  qr_code_url?: string;
  // Phase 1.1 Ship 3 — true for the auto-minted "Generic — no rep"
  // CTA. Read by CTACard for the "Generic" badge + "QR pending"
  // notice; read by duplicate_event RPC to skip cloning generic
  // rows (lets the trigger mint fresh per duplicate).
  is_system_generic?: boolean;
  // Migration 2026-10-29-1260: false = switched off (kept for history; never listed, counted, copied or offered).
  is_active?: boolean;
  // Reward configuration
  reward_type: RewardType;
  // Post-deploy audit follow-up (Batch 3): nullable to align with the
  // submit payloads CTACard/CallToActionTab now send when a CTA's
  // reward_type is flipped away from voucher/offer.
  offer_id?: UUID | null; // Link to existing offer (when reward_type = 'offer')
  voucher_amount?: number | null;
  voucher_start_date?: string | null;
  voucher_end_date?: string | null;
  // Sales rep attribution
  sales_position_id?: UUID | null;
  // Joined data (when fetched with select)
  sales_position?: {
    id: UUID;
    title: string;
    assigned_name: string | null;
    // R2B (2026-05-17): surface inactive state so UI can show
    // "(inactive)" suffix and operators see the staleness signal.
    is_active?: boolean;
  };
  // Voucher expiry
  voucher_expiry_days?: number | null;
  voucher_expires_at_event_end?: boolean; // expire vouchers at the event end instead of expiry_days
  /** Mig 1540: where the voucher can be used (set through set_event_cta_voucher_scope, never by a CTA save). */
  voucher_scope?: 'event' | 'state' | 'national';
  // URL configuration
  include_state_param: boolean;
  /** When true the URL carries usestatedefaults=1. Defaults OFF, unlike include_state_param. */
  use_state_defaults: boolean;
  /** When true (voucher/offer reward) the URL carries singlevoucher=1. Defaults OFF. */
  single_voucher: boolean;
  use_member_signup?: boolean; // When true, use /member-signup/ instead of /voucher/
  display_away_plans?: boolean; // When true, add plantype=away to URL
  // Timestamps
  created_at: string;
  updated_at: string;
  created_by?: UUID;
  updated_by?: UUID;
  // Alias for UI compatibility
  title?: string; // Alias for name
}

/**
 * Input for creating a new CTA
 */
export interface CreateEventCTAInput {
  event_id: UUID;
  name: string;
  cta_type: CTAType;
  base_url: string;
  parameters: Record<string, string>;
  // Reward configuration
  reward_type?: RewardType;
  offer_id?: UUID | null;
  voucher_amount?: number | null;
  voucher_start_date?: string | null;
  voucher_end_date?: string | null;
  // Voucher expiry
  voucher_expiry_days?: number | null;
  voucher_expires_at_event_end?: boolean;
  // Sales rep attribution
  sales_position_id?: UUID | null;
  // URL configuration
  include_state_param?: boolean;
  use_state_defaults?: boolean;
  single_voucher?: boolean;
  use_member_signup?: boolean;
  display_away_plans?: boolean;
}

/**
 * Input for updating an existing CTA
 */
export interface UpdateEventCTAInput {
  name?: string;
  cta_type?: CTAType;
  base_url?: string;
  parameters?: Record<string, string>;
  generated_url?: string;
  qr_code_url?: string;
  // Reward configuration
  reward_type?: RewardType;
  offer_id?: UUID | null;
  voucher_amount?: number | null;
  voucher_start_date?: string | null;
  voucher_end_date?: string | null;
  // Voucher expiry
  voucher_expiry_days?: number | null;
  voucher_expires_at_event_end?: boolean;
  // Sales rep attribution
  sales_position_id?: UUID | null;
  // URL configuration
  include_state_param?: boolean;
  use_state_defaults?: boolean;
  single_voucher?: boolean;
  use_member_signup?: boolean;
  display_away_plans?: boolean;
}

/**
 * Subscription CTA parameter keys
 * Maps form field names to URL parameter names
 */
export const SUBSCRIPTION_CTA_PARAM_KEYS = {
  affid: "affid", // Promoter Affiliate ID
  promo: "promo", // Promo
  altprice: "altprice", // Alt Price
  utm_source: "utm_source", // UTM Source
  utm_term: "utm_term", // UTM Term
  utm_campaign: "utm_campaign", // UTM Campaign
  utm_medium: "utm_medium", // UTM Medium
  utm_content: "utm_content", // UTM Content
  // Auto-populated (not user-editable):
  // ste: event.state (2-char state code)
  // ctaid: CTA UUID (for generate-offer API lookup)
  // discountvalue: voucher amount as numeric (e.g., 20 for $20)
} as const;

/**
 * Subscription CTA base URL - configurable per environment
 * Set via VITE_SUBSCRIPTION_CTA_BASE_URL in environment files
 */
export const SUBSCRIPTION_CTA_BASE_URL =
  import.meta.env.VITE_SUBSCRIPTION_CTA_BASE_URL ||
  "https://ww2.uslawshield.com/voucher/";

/**
 * Base page for the QR / flyer link of a gun show or range night whose venue is in an insurance state (owner
 * 2026-10-09; the database decides which events qualify -- migration 2026-10-09-410, "base_variant"). Same query
 * parameters as the voucher page. Derived from the subscription base so each environment keeps its own host
 * (QA: qa-texas.uslawshield.com/classroom/); VITE_INSURANCE_CTA_BASE_URL overrides it.
 */
export const INSURANCE_CLASSROOM_CTA_BASE_URL =
  import.meta.env.VITE_INSURANCE_CTA_BASE_URL ||
  SUBSCRIPTION_CTA_BASE_URL.replace(/\/voucher\/?$/, "/classroom/");

/**
 * US States for dropdown selection
 */
export const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "PR", name: "Puerto Rico" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
] as const;

// =====================================================
// REPORT SCHEDULE TYPES
// =====================================================

export type AttachmentFormat = "csv" | "xlsx";

export interface ReportSchedule {
  id: string;
  report_type: "event_redemption" | "promoter_summary";
  event_id?: string;
  organization_id?: string;
  recipient_emails: string[];
  scheduled_at?: string;
  /** Date range — present on event_redemption and promoter_summary
   *  schedules created since the 2026-05-25 R9 ship. Nullable so an
   *  operator can leave the range open-ended (use scheduled_at as
   *  the implicit cutoff). */
  date_from?: string | null;
  date_to?: string | null;
  is_recurring: boolean;
  is_active: boolean;
  last_sent_at?: string;
  send_count: number;
  /** Per-schedule attachment format. Defaults to "csv" on the
   *  server (added 2026-06-04). Operator opts in to xlsx via the
   *  Reports tab selector. */
  attachment_format?: AttachmentFormat;
  created_by?: string;
  created_at: string;
  updated_at: string;
}
